import { searchKeys } from "@/lib/query-keys";
import { EmployeeDTO, SavedSearchDTO } from "@shaarp/common";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * A change to some rows of a people table, applied to the whole list. Generic
 * over the row, so it applies alike to the table's own rows and to the saved
 * search's, which carry a few fields more.
 */
export type PeopleRowsUpdate = <Row extends EmployeeDTO>(rows: Row[]) => Row[];

/**
 * Writes a change made in a people table — an email or a phone revealed, a
 * person synced to the CRM — into every saved search of the team the cache
 * holds. The table reads its rows from that cache: left out of it, the change
 * disappears when the table is reopened, or when another table of the
 * conversation that holds the same people is shown, until the cache goes
 * stale.
 *
 * The update only touches the people it names, so a search that does not
 * hold them is left as it is.
 */
export function usePeopleTableRowsWriter(
  teamId: string,
): (update: PeopleRowsUpdate) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (update: PeopleRowsUpdate): void => {
      queryClient.setQueriesData<SavedSearchDTO>(
        { queryKey: searchKeys.team(teamId) },
        (search) =>
          search?.employees
            ? { ...search, employees: update(search.employees) }
            : search,
      );
    },
    [queryClient, teamId],
  );
}
